import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Menu } from './types';

@Component({
    selector: 'app-menu-bar',
    templateUrl: './menu-bar.component.html',
    styleUrls: ['./menu-bar.component.scss'],
    animations: [
        trigger('openClose', [
            state('open', style({
                height: '*',
            })),
            state('closed', style({
                height: 0,
            })),
            transition('open => closed', [animate('0.3s')]),
            transition('closed => open', [animate('0.3s')]),
        ]),
    ],
    standalone: false
})
export class MenuBarComponent implements OnInit, OnChanges, OnDestroy {
    @Input() elements: Menu[] = [];
    active_submenu: number = null;
    private subscriptions = new Subscription();

    @Input() minimize = true;

    @Input() events: Observable<void>;

    constructor(private route: ActivatedRoute, private router: Router) {}

    ngOnInit(): void {
        this.syncActiveSubmenuWithUrl();
        this.subscriptions.add(
            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe(() => this.syncActiveSubmenuWithUrl())
        );
        this.subscriptions.add(this.events.subscribe(() => (this.active_submenu = null)));
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Le menu est construit de façon asynchrone par le parent, et le dépliage du
        // sous-menu n'a de sens que sidebar ouverte : on resynchronise dans les deux cas.
        if (changes.elements || changes.minimize) {
            this.syncActiveSubmenuWithUrl();
        }
    }

    toggle(id: number) {
        if (this.active_submenu === id) this.active_submenu = null;
        else this.active_submenu = id;
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    closeSubmenu() {
        setTimeout(() => {
            this.active_submenu = null;
        }, 100);
    }

    /**
     * Ouvre le groupe de menus correspondant à la page affichée (rechargement, accès direct
     * par URL, navigation). Sans correspondance, l'état courant est conservé.
     * En mode réduit le sous-menu est un survol flottant : on ne l'ouvre pas automatiquement.
     */
    private syncActiveSubmenuWithUrl(): void {
        if (this.minimize) {
            return;
        }
        const index = this.findSubmenuIndexForUrl();
        if (index !== null) {
            this.active_submenu = index;
        }
    }

    /**
     * Les `path` du menu sont relatifs à la route du layout : on les résout comme le fait
     * `routerLink` dans le template, puis on les compare segment par segment à l'URL courante.
     * La correspondance la plus longue gagne, afin que `flux/import-v1/task` l'emporte sur
     * `flux/import-v1/etl` et que `marketing/campaign` ne capture pas `marketing/campaign-group`.
     */
    private findSubmenuIndexForUrl(): number | null {
        const currentSegments = this.toSegments(this.router.url);

        let bestIndex: number = null;
        let bestLength = 0;

        (this.elements || []).forEach((element, index) => {
            (element.pages || []).forEach((page) => {
                const pageSegments = this.toSegments(this.resolvePath(page.path));
                if (pageSegments.length > bestLength && this.startsWith(currentSegments, pageSegments)) {
                    bestLength = pageSegments.length;
                    bestIndex = index;
                }
            });
        });

        return bestIndex;
    }

    /** Chemin absolu d'une entrée de menu, résolu comme le `routerLink` correspondant. */
    private resolvePath(path: string): string {
        return this.router.serializeUrl(this.router.createUrlTree([path], { relativeTo: this.route }));
    }

    private toSegments(url: string): string[] {
        return url.split(/[?#;]/)[0].split('/').filter((segment) => segment.length > 0);
    }

    private startsWith(url: string[], prefix: string[]): boolean {
        return prefix.length <= url.length && prefix.every((segment, index) => segment === url[index]);
    }
}
